<template>
    <v-dialog v-model="show" fullscreen hide-overlay transition="dialog-bottom-transition">
        <v-card class="d-flex flex-column" style="height: 100vh;">
            <v-toolbar dark color="primary" flat>
                <v-btn icon dark @click="closeModal">
                    <v-icon>close</v-icon>
                </v-btn>
                <v-toolbar-title>
                    <v-icon left>history</v-icon>
                    Histórico da Placa {{ board ? board.serialNumber : '' }}
                </v-toolbar-title>
                <v-spacer></v-spacer>
            </v-toolbar>

            <!-- Estatísticas -->
            <v-card-text class="pt-4 flex-shrink-0" v-if="!loadingHistory && history.length > 0">
                <v-row>
                    <v-col cols="12" sm="3">
                        <v-card color="info" dark>
                            <v-card-text class="text-center">
                                <div class="display-1 font-weight-bold">{{ totalTests }}</div>
                                <div class="text-subtitle-1">Total de Testes</div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="12" sm="3">
                        <v-card color="success" dark>
                            <v-card-text class="text-center">
                                <div class="display-1 font-weight-bold">{{ successTests }}</div>
                                <div class="text-subtitle-1">Testes com Sucesso</div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="12" sm="3">
                        <v-card color="error" dark>
                            <v-card-text class="text-center">
                                <div class="display-1 font-weight-bold">{{ failedTests }}</div>
                                <div class="text-subtitle-1">Testes com Falha</div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="12" sm="3">
                        <v-card color="primary" dark>
                            <v-card-text class="text-center">
                                <div class="display-1 font-weight-bold">{{ successRate }}%</div>
                                <div class="text-subtitle-1">Taxa de Sucesso</div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
                <v-divider class="my-4"></v-divider>
            </v-card-text>

            <!-- Filtros -->
            <v-card-text class="pb-0 flex-shrink-0" v-if="!loadingHistory && history.length > 0">
                <v-row>
                    <v-col cols="12" sm="6">
                        <v-select
                            v-model="filterStatus"
                            :items="filterOptions"
                            label="Filtrar por status"
                            outlined
                            dense
                            hide-details
                        ></v-select>
                    </v-col>
                    <v-col cols="12" sm="6" class="text-right">
                        <span class="grey--text">Mostrando {{ displayedHistory.length }} de {{ filteredHistory.length }} testes</span>
                    </v-col>
                </v-row>
            </v-card-text>

            <!-- Conteúdo do Histórico -->
            <v-card-text class="flex-grow-1" style="overflow-y: auto;">
                <div v-if="loadingHistory" class="fill-height d-flex align-center justify-center">
                    <div class="text-center">
                        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
                        <p class="mt-4 grey--text">Carregando histórico...</p>
                    </div>
                </div>
                
                <div v-else-if="history.length === 0" class="fill-height d-flex align-center justify-center">
                    <div class="text-center">
                        <v-icon size="80" color="grey lighten-1">inbox</v-icon>
                        <p class="text-h6 grey--text mt-4">Nenhum histórico encontrado</p>
                        <p class="grey--text">Esta placa ainda não possui testes registrados.</p>
                    </div>
                </div>
                
                <v-timeline v-else dense>
                    <v-timeline-item
                        v-for="(item, index) in displayedHistory"
                        :key="index"
                        :color="getHistoryColor(item)"
                        small
                    >
                        <template v-slot:opposite>
                            <div class="text-caption font-weight-bold">{{ formatDate(item.date) }}</div>
                            <div class="text-caption grey--text">{{ getTimeAgo(item.date) }}</div>
                        </template>
                        
                        <v-expansion-panels flat>
                            <v-expansion-panel class="elevation-3" :class="getCardBorder(item)">
                                <v-expansion-panel-header class="py-2">
                                    <div style="width: 100%;">
                                        <div class="d-flex align-center mb-1">
                                            <v-chip :color="getResultColor(item.result.result)" dark small class="mr-2">
                                                <v-icon left small>{{ getResultIcon(item.result.result) }}</v-icon>
                                                {{ item.result.result === 'success' ? 'Sucesso' : 'Falha' }}
                                            </v-chip>
                                            <span class="text-body-1 font-weight-bold">
                                                Teste {{ item.result.model }} - {{ item.result.boardType }}
                                            </span>
                                            <v-spacer></v-spacer>
                                            <v-chip small outlined class="mr-2">
                                                {{ getStepsSummary(item.result.steps) }}
                                            </v-chip>
                                        </div>
                                        <div class="text-caption grey--text">
                                            <v-icon x-small>access_time</v-icon>
                                            {{ formatDate(item.date) }} - {{ getTimeAgo(item.date) }}
                                        </div>
                                    </div>
                                </v-expansion-panel-header>
                                
                                <v-expansion-panel-content>
                                <div class="text-caption grey--text mb-3">
                                    <v-icon x-small>access_time</v-icon>
                                    Timestamp: {{ formatDate(item.result.timestamp) }}
                                </div>
                                
                                <div class="font-weight-bold mb-2">Etapas do Teste:</div>
                                
                                <v-list dense class="transparent">
                                    <v-list-item
                                        v-for="(step, stepIndex) in item.result.steps"
                                        :key="stepIndex"
                                        class="px-0"
                                    >
                                        <v-list-item-icon class="mr-3">
                                            <v-icon :color="getStepColor(step.status)" small>
                                                {{ getStepIcon(step.status) }}
                                            </v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-content>
                                            <v-list-item-title class="text-body-2">
                                                {{ step.name }}
                                            </v-list-item-title>
                                        </v-list-item-content>
                                        <v-list-item-action>
                                            <v-chip x-small :color="getStepColor(step.status)" dark>
                                                {{ step.status === 'success' ? 'OK' : 'Falha' }}
                                            </v-chip>
                                        </v-list-item-action>
                                    </v-list-item>
                                </v-list>
                                </v-expansion-panel-content>
                            </v-expansion-panel>
                        </v-expansion-panels>
                    </v-timeline-item>
                </v-timeline>
                
                <!-- Botão Carregar Mais -->
                <div v-if="!loadingHistory && hasMoreToShow" class="text-center my-4">
                    <v-btn color="primary" outlined @click="loadMore">
                        <v-icon left>expand_more</v-icon>
                        Carregar mais {{ remainingTests }} testes
                    </v-btn>
                </div>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: 'BoardHistoryModal',
    
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        board: {
            type: Object,
            default: null,
        },
    },

    data() {
        return {
            history: [],
            loadingHistory: false,
            filterStatus: 'all',
            filterOptions: [
                { text: 'Todos os testes', value: 'all' },
                { text: 'Apenas sucessos', value: 'success' },
                { text: 'Apenas falhas', value: 'failed' },
            ],
            itemsPerPage: 10,
            displayLimit: 10,
        };
    },

    computed: {
        show: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            },
        },
        
        totalTests() {
            return this.history.length;
        },
        
        successTests() {
            return this.history.filter(item => item.result && item.result.result === 'success').length;
        },
        
        failedTests() {
            return this.history.filter(item => item.result && item.result.result !== 'success').length;
        },
        
        successRate() {
            if (this.totalTests === 0) return 0;
            return Math.round((this.successTests / this.totalTests) * 100);
        },
        
        filteredHistory() {
            let filtered = [];
            if (this.filterStatus === 'all') {
                filtered = [...this.history];
            } else if (this.filterStatus === 'success') {
                filtered = this.history.filter(item => item.result && item.result.result === 'success');
            } else if (this.filterStatus === 'failed') {
                filtered = this.history.filter(item => item.result && item.result.result !== 'success');
            } else {
                filtered = [...this.history];
            }
            
            // Ordenar por data, do mais novo para o mais antigo
            return filtered.sort((a, b) => {
                const dateA = new Date(a.date);
                const dateB = new Date(b.date);
                return dateB - dateA; // Ordem decrescente (mais novo primeiro)
            });
        },
        
        displayedHistory() {
            return this.filteredHistory.slice(0, this.displayLimit);
        },
        
        hasMoreToShow() {
            return this.displayLimit < this.filteredHistory.length;
        },
        
        remainingTests() {
            return this.filteredHistory.length - this.displayLimit;
        },
    },

    watch: {
        value(newVal) {
            if (newVal && this.board) {
                this.fetchHistory();
            }
        },
        filterStatus() {
            this.displayLimit = this.itemsPerPage;
        },
    },

    methods: {
        closeModal() {
            this.show = false;
            this.history = [];
            this.displayLimit = this.itemsPerPage;
            this.filterStatus = 'all';
        },
        
        loadMore() {
            this.displayLimit += this.itemsPerPage;
        },
        
        fetchHistory() {
            if (!this.board || !this.board.id) return;
            
            this.loadingHistory = true;
            this.history = [];
            
            this.$http
                .get(`boards/history/${this.board.id}`)
                .then((response) => {
                    // O interceptor HTTP já retorna response.data, então acessamos response.history diretamente
                    this.history = response.history || [];
                    this.loadingHistory = false;
                })
                .catch((error) => {
                    console.error("Erro ao buscar histórico:", error);
                    this.$eventHub.$emit("msgError", "Erro ao buscar histórico.");
                    this.loadingHistory = false;
                    this.closeModal();
                });
        },
        
        formatDate(date) {
            if (!date) return '';
            return new Date(date).toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
            });
        },
        
        getTimeAgo(date) {
            if (!date) return '';
            const now = new Date();
            const testDate = new Date(date);
            const diffMs = now - testDate;
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);
            const diffDays = Math.floor(diffMs / 86400000);
            
            if (diffMins < 1) return 'Agora mesmo';
            if (diffMins < 60) return `${diffMins} min atrás`;
            if (diffHours < 24) return `${diffHours}h atrás`;
            if (diffDays < 30) return `${diffDays} dias atrás`;
            return `${Math.floor(diffDays / 30)} meses atrás`;
        },
        
        getHistoryColor(item) {
            if (!item.result) return 'blue';
            return item.result.result === 'success' ? 'green' : 'red';
        },
        
        getResultColor(result) {
            return result === 'success' ? 'success' : 'error';
        },
        
        getResultIcon(result) {
            return result === 'success' ? 'check_circle' : 'error';
        },
        
        getStepColor(status) {
            return status === 'success' ? 'success' : 'error';
        },
        
        getStepIcon(status) {
            return status === 'success' ? 'check_circle' : 'cancel';
        },
        
        getStepsSummary(steps) {
            if (!steps) return '0/0';
            const successCount = steps.filter(s => s.status === 'success').length;
            return `${successCount}/${steps.length} etapas`;
        },
        
        getCardBorder(item) {
            if (!item.result) return '';
            return item.result.result === 'success' ? 'success-border' : 'error-border';
        },
    },
};
</script>

<style scoped>
.success-border {
    border-left: 4px solid #4caf50 !important;
}

.error-border {
    border-left: 4px solid #f44336 !important;
}

.v-timeline::before {
    background: rgba(0, 0, 0, 0.12);
}

.v-expansion-panel::before {
    box-shadow: none;
}

.v-expansion-panel {
    border-radius: 4px !important;
}

.v-expansion-panel-header {
    min-height: 48px !important;
}
</style>
